/**
 * Radio Primitive
 * Form radio button component with group support and design system tokens
 */

import React from "react";
import {
  Radio as ChakraRadio,
  RadioGroup as ChakraRadioGroup,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Stack,
  Text,
} from "@chakra-ui/react";
import { resolveSpacing } from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for radio tokens
declare const __radio_size_brand: unique symbol;
declare const __radio_state_brand: unique symbol;
declare const __radio_direction_brand: unique symbol;

export interface RadioSizeToken {
  readonly [__radio_size_brand]: true;
  readonly _value: string;
}

export interface RadioStateToken {
  readonly [__radio_state_brand]: true;
  readonly _value: string;
}

export interface RadioDirectionToken {
  readonly [__radio_direction_brand]: true;
  readonly _value: string;
}

// Factory functions
const createRadioSizeToken = (value: string): RadioSizeToken => ({ _value: value } as RadioSizeToken);
const createRadioStateToken = (value: string): RadioStateToken => ({ _value: value } as RadioStateToken);
const createRadioDirectionToken = (value: string): RadioDirectionToken => ({ _value: value } as RadioDirectionToken);

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  helperText?: string;
}

export interface RadioProps {
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: RadioSizeToken;
  state?: RadioStateToken;
  value?: string;
  isChecked?: boolean;
  defaultChecked?: boolean;
  isDisabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
  "data-testid"?: string;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  size?: RadioSizeToken;
  state?: RadioStateToken;
  direction?: RadioDirectionToken;
  isDisabled?: boolean;
  isRequired?: boolean;
  "data-testid"?: string;
}

function RadioComponent({
  label,
  helperText,
  errorText,
  size,
  state,
  value,
  isChecked,
  defaultChecked,
  isDisabled = false,
  onChange,
  onBlur,
  onFocus,
  name,
  id,
  "data-testid": testId,
}: RadioProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedSize = size || radioTokens.size.medium;
  const resolvedState = state || radioTokens.state.default;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;
  const isSuccess = resolvedState._value === "success";
  const isWarning = resolvedState._value === "warning";

  // Get state colors
  const getStateColors = () => {
    if (isInvalid) {
      return {
        colorScheme: "red",
        borderColor: useColorModeValue(Color.primitive.red(500)),
      };
    } else if (isSuccess) {
      return {
        colorScheme: "green",
        borderColor: useColorModeValue(Color.primitive.green(500)),
      };
    } else if (isWarning) {
      return {
        colorScheme: "orange",
        borderColor: useColorModeValue(Color.primitive.orange(500)),
      };
    }

    return {
      colorScheme: "blue",
      borderColor: useColorModeValue(Color.border.primary),
    };
  };

  const getSizeProps = (size: RadioSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
        };
    }
  };

  const stateColors = getStateColors();
  const sizeProps = getSizeProps(resolvedSize);

  return (
    <FormControl isInvalid={isInvalid}>
      <ChakraRadio
        {...sizeProps}
        {...stateColors}
        value={value}
        isChecked={isChecked}
        defaultChecked={defaultChecked}
        isDisabled={isDisabled}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        name={name}
        id={id}
        data-testid={testId}
        _focus={{
          boxShadow: `0 0 0 2px ${useColorModeValue(Color.interactive.primary)}`,
        }}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
        }}
      >
        {label && (
          <Text
            color={useColorModeValue(Color.text.primary)}
            fontSize={sizeProps.fontSize}
          >
            {label}
          </Text>
        )}
      </ChakraRadio>

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function RadioGroupComponent({
  options,
  value,
  defaultValue,
  onChange,
  name,
  label,
  helperText,
  errorText,
  size,
  state,
  direction,
  isDisabled = false,
  isRequired = false,
  "data-testid": testId,
}: RadioGroupProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedSize = size || radioTokens.size.medium;
  const resolvedState = state || radioTokens.state.default;
  const resolvedDirection = direction || radioTokens.direction.vertical;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;

  const getSizeProps = (size: RadioSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
        };
    }
  };

  const sizeProps = getSizeProps(resolvedSize);

  return (
    <FormControl isInvalid={isInvalid} isRequired={isRequired}>
      {label && (
        <FormLabel
          fontSize={sizeProps.fontSize}
          color={useColorModeValue(Color.text.primary)}
          mb={resolveSpacing(Spacing.tiny)}
        >
          {label}
        </FormLabel>
      )}

      <ChakraRadioGroup
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        name={name}
        isDisabled={isDisabled}
        data-testid={testId}
      >
        <Stack
          direction={resolvedDirection._value === "horizontal" ? "row" : "column"}
          spacing={resolveSpacing(Spacing.small)}
        >
          {options.map((option) => (
            <ChakraRadio
              key={option.value}
              value={option.value}
              isDisabled={isDisabled || option.disabled}
              {...sizeProps}
              colorScheme="blue"
              _focus={{
                boxShadow: `0 0 0 2px ${useColorModeValue(Color.interactive.primary)}`,
              }}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
              }}
            >
              <Text
                color={useColorModeValue(Color.text.primary)}
                fontSize={sizeProps.fontSize}
              >
                {option.label}
              </Text>
              {option.helperText && (
                <Text
                  fontSize="xs"
                  color={useColorModeValue(Color.text.secondary)}
                  mt={1}
                >
                  {option.helperText}
                </Text>
              )}
            </ChakraRadio>
          ))}
        </Stack>
      </ChakraRadioGroup>

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Radio tokens
const radioTokens = {
  size: {
    small: createRadioSizeToken("small"),
    medium: createRadioSizeToken("medium"),
    large: createRadioSizeToken("large"),
  } as const,

  state: {
    default: createRadioStateToken("default"),
    success: createRadioStateToken("success"),
    warning: createRadioStateToken("warning"),
    error: createRadioStateToken("error"),
  } as const,

  direction: {
    vertical: createRadioDirectionToken("vertical"),
    horizontal: createRadioDirectionToken("horizontal"),
  } as const,
};

// Public API - combines tokens and component with compound component
const RadioWithGroup = Object.assign(RadioComponent, radioTokens);
RadioWithGroup.Group = RadioGroupComponent;

export const Radio = RadioWithGroup;
